/**
 * Typed access to the backend.
 *
 * A token is acquired per request rather than held in state: MSAL already caches
 * and renews, so keeping our own copy would just be a second, staler cache.
 *
 * Errors carry the backend's `reason` code so the interface can say what actually
 * happened. The API returns the same 404 for "does not exist" and "belongs to
 * someone else" on purpose, so the client cannot distinguish them either.
 */

import { getAccessToken } from "../auth/msal";
import type {
  ActorDetail,
  ActorRow,
  AppConfig,
  AskResponse,
  Bucket,
  Citation,
  ConversationDetail,
  ConversationSummary,
  DocumentRecord,
  DocumentUsage,
  Feedback,
  JobStatus,
  Policy,
  QuestionRow,
  UsageSummary,
  UploadAccepted,
  WhoAmI,
} from "./types";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly reason: string,
    message: string,
    /**
     * The whole `detail` object, when the backend sent one.
     *
     * Only `reason` used to survive, so the fields the API deliberately includes
     * were thrown away at the boundary: a 422 from the guardrails panel carries
     * `field`, `min` and `max`, and the panel could only say "some value is out of
     * range" — which, for the prompt-length error, points at the wrong control.
     */
    readonly detail: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const MESSAGES: Record<string, string> = {
  not_found: "No se ha encontrado.",
  insufficient_role: "Tu cuenta no tiene permiso para esta acción.",
  unsupported_file_type: "Ese tipo de archivo no se puede procesar.",
  file_too_large: "El archivo supera el tamaño máximo permitido.",
  empty_file: "El archivo está vacío.",
  slug_already_used: "Ya existe una colección con ese identificador.",
  not_an_access_token: "La sesión no es válida para esta API. Vuelve a entrar.",
  missing_bearer_token: "Sesión caducada. Vuelve a entrar.",
  token_expired: "Sesión caducada. Vuelve a entrar.",
  group_claim_overage:
    "Tu cuenta pertenece a demasiados grupos y no se pueden leer tus permisos. Avisa a Sistemas.",
};

const GENERATION_FAILED = "No se ha podido generar la respuesta. Vuelve a intentarlo.";
const STREAM_INTERRUPTED =
  "La conexión se ha cortado a mitad de respuesta. Vuelve a intentarlo.";

/**
 * Whether the backend is running its local-only Entra bypass.
 *
 * Read from `/api/config` once at startup. It defaults to `false`, so a config
 * that fails to load leaves every request attaching a token as usual: the
 * degraded path is the authenticated one, never the anonymous one.
 */
let authDisabled = false;

export function configureAuth(config: AppConfig): void {
  authDisabled = config.authDisabled;
}

async function authHeaders(): Promise<Record<string, string>> {
  if (authDisabled) return {};
  return { Authorization: `Bearer ${await getAccessToken()}` };
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  for (const [name, value] of Object.entries(await authHeaders())) {
    headers.set(name, value);
  }
  if (init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`/api${path}`, { ...init, headers });
  if (response.status === 204) {
    return undefined as T;
  }
  if (!response.ok) {
    throw await toError(response);
  }
  return (await response.json()) as T;
}

async function toError(response: Response): Promise<ApiError> {
  let reason = "unknown";
  let detail: Record<string, unknown> = {};
  try {
    const body = (await response.json()) as {
      detail?: Record<string, unknown> | string;
    };
    if (typeof body.detail === "object" && body.detail !== null) {
      detail = body.detail;
      if (typeof body.detail.reason === "string") reason = body.detail.reason;
    }
  } catch {
    // Not JSON: a proxy or gateway error. The status is all we have.
  }
  return new ApiError(
    response.status,
    reason,
    MESSAGES[reason] ?? byStatus(response.status),
    detail,
  );
}

/**
 * A sentence for a status with no `reason` the backend named.
 *
 * The fallback used to be `Error 502 al contactar con el servidor.`, which hands
 * the user an HTTP status code and nothing they can act on. The code is still on
 * `ApiError.status` for logging; it just stops being the message.
 */
function byStatus(status: number): string {
  if (status === 401 || status === 403) return "Sesión caducada o sin permiso. Vuelve a entrar.";
  if (status === 404) return "No se ha encontrado.";
  if (status === 409) return "Ese cambio choca con el estado actual. Recarga y vuelve a intentarlo.";
  if (status === 413) return "El archivo supera el tamaño máximo permitido.";
  if (status === 422) return "Algún dato enviado no es válido.";
  if (status === 429) return "Demasiadas peticiones seguidas. Espera unos segundos.";
  if (status === 503 || status === 504) {
    return "El servicio no responde ahora mismo. Vuelve a intentarlo en un momento.";
  }
  if (status >= 500) return "Error en el servidor. Vuelve a intentarlo; si sigue, avisa a Sistemas.";
  if (status >= 400) return "No se ha podido completar la acción.";
  return "Error inesperado.";
}

/**
 * The message to show for anything thrown by this module.
 *
 * Lived as a private copy in five page components, which is why the raw-status
 * fallback had to be fixed in one place and could not be.
 */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Error inesperado.";
}

export interface StreamHandlers {
  /** Fires once, before any text: the sources the answer will draw on. */
  onCitations: (citations: Citation[]) => void;
  /** Fires per fragment of answer text, in order. */
  onDelta: (text: string) => void;
  /**
   * Fires once the answer is complete, with the 1-based positions it referenced.
   *
   * It has to be a second event: `citations` goes out before the model writes a
   * word, so at that point nothing is known about which of them the answer will
   * lean on. The positions come from the server rather than being parsed here so
   * that the numbering rule lives in one place.
   */
  onCited: (positions: number[]) => void;
}

interface SseEvent {
  name: string;
  data: Record<string, unknown>;
}

function parseSseBlock(block: string): SseEvent | null {
  let name = "";
  let data = "";
  for (const line of block.split("\n")) {
    if (line.startsWith("event: ")) name = line.slice("event: ".length);
    else if (line.startsWith("data: ")) data = line.slice("data: ".length);
  }
  if (!name || !data) return null;
  return { name, data: JSON.parse(data) as Record<string, unknown> };
}

/**
 * The ask flow over server-sent events. Resolves once the server confirms the
 * answer is stored (`done`); rejects on transport errors, an in-band `error`
 * event, or a stream that ends without `done`.
 */
async function askStream(
  conversationId: string,
  question: string,
  bucketIds: string[],
  handlers: StreamHandlers,
): Promise<void> {
  const response = await fetch(`/api/conversations/${conversationId}/messages/stream`, {
    method: "POST",
    headers: {
      ...(await authHeaders()),
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    },
    body: JSON.stringify({ question, bucket_ids: bucketIds }),
  });
  if (!response.ok || !response.body) {
    throw await toError(response);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finished = false;

  const handle = (event: SseEvent) => {
    if (event.name === "delta") {
      handlers.onDelta(event.data.text as string);
    } else if (event.name === "citations") {
      handlers.onCitations(event.data.citations as Citation[]);
    } else if (event.name === "cited") {
      handlers.onCited((event.data.positions as number[]) ?? []);
    } else if (event.name === "done") {
      finished = true;
    } else if (event.name === "error") {
      throw new ApiError(200, "generation_failed", GENERATION_FAILED);
    }
  };

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let separator;
    while ((separator = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, separator);
      buffer = buffer.slice(separator + 2);
      const event = parseSseBlock(block);
      if (event) handle(event);
    }
  }

  if (!finished) {
    throw new ApiError(0, "stream_interrupted", STREAM_INTERRUPTED);
  }
}

/** Public: served before sign-in, so it carries no token. */
export async function fetchConfig(): Promise<AppConfig> {
  const response = await fetch("/api/config");
  if (!response.ok) throw new Error("no se ha podido cargar la configuración");
  return (await response.json()) as AppConfig;
}

/**
 * Ventanas que el backend acepta; cualquier otra devuelve 422.
 *
 * No se llama `Window` a propósito: eso sombrearía el tipo global del DOM dentro
 * de cualquier módulo que lo importe.
 */
export type RangeDays = 7 | 30 | 90 | 365;

export const api = {
  me: () => request<WhoAmI>("/me"),

  buckets: () => request<Bucket[]>("/buckets"),

  createBucket: (payload: { name: string; slug: string; description: string | null }) =>
    request<Bucket>("/buckets", { method: "POST", body: JSON.stringify(payload) }),

  deleteBucket: (bucketId: string) =>
    request<void>(`/buckets/${bucketId}`, { method: "DELETE" }),

  /** Changes the display name only; the slug is immutable. */
  renameBucket: (bucketId: string, name: string) =>
    request<Bucket>(`/buckets/${bucketId}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),

  bucketPrompt: (bucketId: string) =>
    request<{ prompt_template: string | null }>(`/buckets/${bucketId}/prompt`),

  /** Empty or null restores the built-in template. Applies on the next question. */
  setBucketPrompt: (bucketId: string, promptTemplate: string | null) =>
    request<{ prompt_template: string | null }>(`/buckets/${bucketId}/prompt`, {
      method: "PUT",
      body: JSON.stringify({ prompt_template: promptTemplate }),
    }),

  conversations: () => request<ConversationSummary[]>("/conversations"),

  conversation: (id: string) => request<ConversationDetail>(`/conversations/${id}`),

  createConversation: (bucketId: string | null) =>
    request<ConversationSummary>("/conversations", {
      method: "POST",
      body: JSON.stringify({ bucket_id: bucketId }),
    }),

  deleteConversation: (id: string) =>
    request<void>(`/conversations/${id}`, { method: "DELETE" }),

  /**
   * Rates an answer. Idempotent: the row is unique on (user, message) and the
   * server updates in place, so clicking again just changes one's mind.
   */
  rateAnswer: (
    conversationId: string,
    messageId: string,
    isPositive: boolean,
    comment = "",
  ) =>
    request<Feedback>(
      `/conversations/${conversationId}/messages/${messageId}/feedback`,
      { method: "POST", body: JSON.stringify({ is_positive: isPositive, comment }) },
    ),

  /** Undoes a soft delete; only the owner's own deleted conversations qualify. */
  restoreConversation: (id: string) =>
    request<ConversationSummary>(`/conversations/${id}/restore`, { method: "POST" }),

  /** POST with a body: the question must not end up in access logs or history. */
  ask: (conversationId: string, question: string, bucketIds: string[]) =>
    request<AskResponse>(`/conversations/${conversationId}/messages`, {
      method: "POST",
      body: JSON.stringify({ question, bucket_ids: bucketIds }),
    }),

  askStream,

  documents: (bucketId: string) =>
    request<DocumentRecord[]>(`/buckets/${bucketId}/documents`),

  documentStatus: (documentId: string) =>
    request<JobStatus>(`/documents/${documentId}/status`),

  deleteDocument: (documentId: string) =>
    request<void>(`/documents/${documentId}`, { method: "DELETE" }),

  upload: (bucketId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<UploadAccepted>(`/buckets/${bucketId}/documents`, {
      method: "POST",
      body: form,
    });
  },
  /* --- Administración. Todo esto exige rag.admin en el backend. --------- */

  adminSummary: (days: RangeDays) => request<UsageSummary>(`/admin/summary?days=${days}`),

  adminDocuments: (days: RangeDays) =>
    request<DocumentUsage>(`/admin/documents?days=${days}`),

  adminUsers: (days: RangeDays, includeHidden = false) =>
    request<{ days: number; users: ActorRow[] }>(
      `/admin/users?days=${days}&include_hidden=${includeHidden}`,
    ),

  adminUser: (userId: string, days: RangeDays) =>
    request<ActorDetail>(`/admin/users/${userId}?days=${days}`),

  adminQuestions: (days: RangeDays) =>
    request<{ days: number; questions: QuestionRow[] }>(
      `/admin/questions?days=${days}`,
    ),

  adminPolicy: () => request<Policy>("/admin/policy"),

  /**
   * `null` en un campo numérico lo devuelve al valor del despliegue.
   *
   * Enviar `level` fija los cuatro números de su preset e ignora los que vayan al
   * lado; el backend lo resuelve así a propósito. Para salir de la escala hay que
   * enviar `level: null` con los números explícitos.
   */
  adminSetPolicy: (values: {
    level?: number | null;
    prompt_override?: string | null;
    [field: string]: number | string | null | undefined;
  }) =>
    request<Policy>("/admin/policy", {
      method: "PUT",
      body: JSON.stringify(values),
    }),

  adminSetHidden: (userId: string, hidden: boolean) =>
    request<void>(`/admin/users/${userId}/hidden?hidden=${hidden}`, {
      method: "PATCH",
    }),
};
